import { DataAccess } from "../../../dataaccess/data.access";
import type { S25ItemI } from "../../../pojo/S25ItemI";

export interface ElectionHeader {
    electionId: number;
    name: string;
    descr?: string;
    votingStartDt: string;
    votingEndDt: string;
    nominationStartDt?: string;
    nominationEndDt?: string;
    acctId?: number | null;
    candidateOrder: "SORT_ORDER" | "ALPHA" | "RANDOM";
    candidateIngestionPointId?: number | null;
}

export interface ElectionDto {
    electionId?: number;
    name: string;
    descr?: string;
    nominationStartDt: string;
    nominationEndDt: string;
    votingStartDt: string;
    votingEndDt: string;
    candidateIngestionPointId?: number | null;
    acctId?: number | null;
    candidateOrder: "SORT_ORDER" | "ALPHA" | "RANDOM";
    deleted?: boolean;
    /**
     * Present on {@link ElectionApi.getElection} (nested GDA: positions, voter rules, and per-position
     * {@link ElectionPositionDto#candidates}).
     * Vote rows are never returned from the API.
     */
    positions?: ElectionPositionDto[];
}

/** Filled on {@link ElectionApi.getElection} (nested GDA: {@link ElectionPositionDto#candidates}). */
export interface ElectionPositionCandidateDto {
    electionCandidateId?: number;
    electionId?: number;
    electionPositionId?: number;
    contId: number;
    /** Rich text (HTML) */
    campaignBrief?: string;
    photoUrl?: string;
    sortOrder?: number;
    createDt?: string;
}

export interface ElectionPositionDto {
    electionPositionId?: number;
    electionId?: number;
    positionTitle: string;
    descr?: string;
    sortOrder?: number;
    createDt?: string;
    /** Filled on {@link ElectionApi.getElection} alongside {@link ElectionDto#positions}. */
    voterRules?: ElectionVoterRuleDto[];
    candidates?: ElectionPositionCandidateDto[];
}

export type VoterRuleType = "ALL" | "MEMBERS" | "ROLE";

export interface ElectionVoterRuleDto {
    electionPositionVoterRuleId?: number;
    electionPositionId?: number;
    electionId?: number;
    ruleType: VoterRuleType;
    roleId?: number | null;
}

export interface BallotApiResponse {
    election: ElectionHeader;
    positions: {
        electionPositionId: number;
        positionTitle: string;
        descr?: string;
        sortOrder: number;
        candidates: {
            electionCandidateId: number;
            contId: number;
            firstName: string;
            lastName: string;
            photoUrl?: string;
            campaignBrief?: string;
            /** For {@link ElectionHeader#candidateOrder} {@code SORT_ORDER} display ordering. */
            sortOrder: number;
        }[];
    }[];
    selectedCandidateContIdByPosition: Record<string, number>;
}

export interface PositionResultDto {
    orgName?: string;
    rows: { contId: number; name: string; email: string; voteCount: number }[];
}

export interface TiedCandidateDto {
    contId: number;
    name: string;
    email: string;
    voteCount: number;
}

export interface WinnerRowDto {
    positionTitle: string;
    winnerName: string;
    winnerEmail: string;
    tie: boolean;
    positionSortOrder: number;
    /** Sole winner tally, shared top tally when tied, or 0 when no votes yet. */
    winnerVoteCount: number;
    /** Tied for first; name/email/voteCount per person (school decides if that counts as shared office). */
    tiedCandidates?: TiedCandidateDto[] | null;
}

export interface WinnersDto {
    orgName?: string;
    rows: WinnerRowDto[];
}

/** Result of {@link ElectionApi.getVotingOpen}. */
export interface VotingOpenDto {
    votingOpen: boolean;
    /** ISO datetime when voting ends; user timezone from API. */
    votingEndDt?: string;
}

export class ElectionApi {
    static getEligible() {
        return DataAccess.get<ElectionHeader[]>("/elections/eligible.json");
    }

    static getOpenNominations() {
        return DataAccess.get<ElectionHeader[]>("/elections/open-nominations.json");
    }

    static getBallot(electionId: number) {
        return DataAccess.get<BallotApiResponse>(`/elections/${electionId}/ballot.json`);
    }

    /** POST body root key must be `VoteRequest` (Jackson UNWRAP_ROOT_VALUE + server ObjectMapper). */
    static postVote(electionId: number, positionId: number, candidateContId: number) {
        return DataAccess.post<void>(`/elections/${electionId}/positions/${positionId}/vote.json`, {
            VoteRequest: { candidateContId },
        });
    }

    static submit(electionId: number) {
        return DataAccess.post<void>(`/elections/${electionId}/submit.json`, {});
    }

    static listElections() {
        return DataAccess.get<ElectionDto[]>("/elections/list.json");
    }

    static getElection(electionId: number) {
        return DataAccess.get<ElectionDto>(`/elections/${electionId}.json`);
    }

    static createElection(body: ElectionDto) {
        return DataAccess.post<ElectionDto>("/elections/election.json", body);
    }

    static updateElection(electionId: number, body: ElectionDto) {
        return DataAccess.put<void>(`/elections/${electionId}.json`, { ...body, electionId });
    }

    static deleteElection(electionId: number) {
        return DataAccess.delete<void>(`/elections/${electionId}.json`);
    }

    static copyElection(electionId: number) {
        return DataAccess.post<ElectionDto>(`/elections/${electionId}/copy.json`, {});
    }

    static listPositions(electionId: number) {
        return DataAccess.get<ElectionPositionDto[]>(`/elections/${electionId}/positions.json`);
    }

    /**
     * Instance-wide list for search-criteria (labels include election name; see {@code ElectionService#listAllElectionPositionsForSearch}).
     */
    static searchPositionsForInstanceSearchCriteria() {
        return DataAccess.get<S25ItemI[]>(
            DataAccess.injectCaller(
                "/elections/search/positions.json",
                "ElectionApi.searchPositionsForInstanceSearchCriteria",
            ),
        );
    }

    /** Batch-resolve position titles by id (same order as request body). */
    static getPositionTitles(positionIds: number[]) {
        if (!positionIds?.length) {
            return Promise.resolve([] as S25ItemI[]);
        }
        return DataAccess.post<S25ItemI[]>(
            DataAccess.injectCaller("/elections/position-titles.json", "ElectionApi.getPositionTitles"),
            positionIds,
        );
    }

    static createPosition(electionId: number, body: ElectionPositionDto) {
        return DataAccess.post<ElectionPositionDto>(`/elections/${electionId}/positions.json`, body);
    }

    static updatePosition(electionId: number, positionId: number, body: ElectionPositionDto) {
        return DataAccess.put<void>(`/elections/${electionId}/positions/${positionId}.json`, {
            ...body,
            electionPositionId: positionId,
            electionId,
        });
    }

    static deletePosition(positionId: number) {
        return DataAccess.delete<void>(`/elections/positions/${positionId}.json`);
    }

    static addVoterRule(electionId: number, positionId: number, body: ElectionVoterRuleDto) {
        return DataAccess.post<ElectionVoterRuleDto>(
            `/elections/${electionId}/positions/${positionId}/voter-rules.json`,
            body,
        );
    }

    static deleteVoterRule(ruleId: number) {
        return DataAccess.delete<void>(`/elections/voter-rules/${ruleId}.json`);
    }

    /** POST body root key must be `CandidateWriteDto` (Jackson UNWRAP_ROOT_VALUE + server ObjectMapper). */
    static upsertCandidate(
        electionId: number,
        positionId: number,
        body: { contId: number; campaignBrief?: string; photoUrl?: string },
    ) {
        return DataAccess.post<unknown>(`/elections/${electionId}/positions/${positionId}/candidates.json`, {
            CandidateWriteDto: body,
        });
    }

    static getPositionResults(electionId: number, positionId: number) {
        return DataAccess.get<PositionResultDto>(`/elections/${electionId}/positions/${positionId}/results.json`);
    }

    static getWinners(electionId: number) {
        return DataAccess.get<WinnersDto>(`/elections/${electionId}/winners.json`);
    }

    static getVotingOpen(electionId: number) {
        return DataAccess.get<VotingOpenDto>(`/elections/${electionId}/voting-open.json`);
    }
}
